import AddIcon from "@mui/icons-material/Add";
import Alert from "@mui/material/Alert";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import ButtonBase from "@mui/material/ButtonBase";
import Chip from "@mui/material/Chip";
import CircularProgress from "@mui/material/CircularProgress";
import Divider from "@mui/material/Divider";
import Paper from "@mui/material/Paper";
import Stack from "@mui/material/Stack";
import Typography from "@mui/material/Typography";
import { useTheme } from "@mui/material/styles";
import { formatMoney, parseDecimal } from "@bw/shared/utils";
import { useMemo } from "react";
import { Navigate, useNavigate } from "react-router-dom";
import {
  Area,
  AreaChart,
  CartesianGrid,
  ResponsiveContainer,
  Tooltip,
  XAxis,
} from "recharts";

import { useMyOrg } from "@/hooks/use-my-org";
import {
  useOrgFacilitiesQuery,
  useSelectedFacility,
} from "@/app/FacilityContext";
import {
  useOrgRevenue,
  useOrgMemberCount,
  useOrgRevenueTrend,
  useOrgMemberTrend,
  useSummaryRange,
  useTrendRange,
} from "@/hooks/use-org-analytics";
import type { FacilityListItem } from "@/libs/facilityApi";

/**
 * OrgDashboardPage (route /org) — the org-wide rollup across every facility.
 *
 * It sits at an org-level route, so it owns its OWN status check: a non-approved
 * org redirects to /getting-started rather than rendering operator analytics.
 *
 * Trend cards are Recharts area charts styled with the active MUI theme.
 * Money is a Decimal (number | string) → formatMoney,
 * never raw arithmetic. Missing revenue/currency remains unknown; an absent or
 * failed historical trend is never replaced by an invented "Now" data point.
 */

const UNLOCKED = new Set(["approved", "active"]);

const STATUS_COLOR: Record<string, "success" | "warning" | "default"> = {
  active: "success",
  pending: "warning",
  suspended: "default",
};

interface TrendPoint {
  label: string;
  value: number;
}

function monthLabel(iso: string): string {
  return new Intl.DateTimeFormat("en-US", { month: "short" }).format(
    new Date(iso),
  );
}

const numberFormatter = new Intl.NumberFormat("en-US");

function TrendCard({
  label,
  value,
  data,
  color,
  formatTooltip,
}: {
  label: string;
  value: string;
  data: TrendPoint[];
  color: string;
  formatTooltip: (v: number) => string;
}) {
  return (
    <Paper
      component="section"
      aria-label={label}
      variant="outlined"
      sx={{ minHeight: 288, overflow: "hidden" }}
    >
      <Stack
        direction="row"
        sx={{
          alignItems: "flex-start",
          justifyContent: "space-between",
          p: 2.5,
        }}
      >
        <Box>
          <Typography color="text.secondary" variant="overline">
            {label}
          </Typography>
          <Typography
            component="p"
            variant="h5"
            sx={{
              fontWeight: 800,
              fontVariantNumeric: "tabular-nums",
              mt: 0.25,
            }}
          >
            {value}
          </Typography>
        </Box>
        <Chip label="6M" size="small" variant="outlined" />
      </Stack>
      <Box sx={{ height: 170, minWidth: 0, px: 1.5 }}>
        <ResponsiveContainer height="100%" width="100%">
          <AreaChart data={data} margin={{ left: 8, right: 8, top: 8 }}>
            <CartesianGrid strokeDasharray="3 3" vertical={false} />
            <XAxis dataKey="label" hide />
            <Tooltip
              cursor={false}
              formatter={(tooltipValue) => formatTooltip(Number(tooltipValue))}
            />
            <Area
              dataKey="value"
              fill={color}
              fillOpacity={0.16}
              stroke={color}
              strokeWidth={2}
              type="monotone"
            />
          </AreaChart>
        </ResponsiveContainer>
        {/* Period axis caption. Distinct from the SVG ticks (which don't render
            without layout) so the bucket labels — including the zero-data "Now"
            placeholder — are always present, honest, and screen-reader-readable. */}
        <Stack
          direction="row"
          sx={{ justifyContent: "space-between", mt: 0.5 }}
        >
          {data.map((p, i) => (
            <Typography
              color="text.secondary"
              key={`${p.label}-${i}`}
              variant="caption"
            >
              {p.label}
            </Typography>
          ))}
        </Stack>
      </Box>
    </Paper>
  );
}

function FacilityRow({
  facility,
  onSelect,
}: {
  facility: FacilityListItem;
  onSelect: () => void;
}) {
  return (
    <ButtonBase
      type="button"
      onClick={onSelect}
      sx={{
        alignItems: "center",
        display: "flex",
        gap: 2,
        justifyContent: "space-between",
        px: { xs: 2, lg: 3 },
        py: 1.5,
        textAlign: "left",
        width: "100%",
        "&:hover": { bgcolor: "action.hover" },
      }}
    >
      <Box sx={{ minWidth: 0 }}>
        <Typography noWrap variant="body2" sx={{ fontWeight: 700 }}>
          {facility.name}
        </Typography>
        <Typography color="text.secondary" noWrap variant="body2">
          {facility.location || "No location set"}
        </Typography>
      </Box>
      <Chip
        color={STATUS_COLOR[facility.status] ?? "default"}
        label={facility.status}
        size="small"
        sx={{ flexShrink: 0, textTransform: "capitalize" }}
        variant="outlined"
      />
    </ButtonBase>
  );
}

export function OrgDashboardPage() {
  const theme = useTheme();
  const navigate = useNavigate();
  const { setFacility } = useSelectedFacility();

  const orgView = useMyOrg();
  const orgId = orgView.org?.org_id;
  const status = orgView.org?.status ?? "";
  const canView = UNLOCKED.has(status);
  const dashboardOrgId = canView ? orgId : undefined;

  const summaryRange = useSummaryRange();
  const trendRange = useTrendRange();

  const { data: facilities, isLoading: facilitiesLoading } =
    useOrgFacilitiesQuery(dashboardOrgId);
  const { data: revenue, isError: revenueError } = useOrgRevenue(
    dashboardOrgId,
    summaryRange.start,
    summaryRange.end,
  );
  const { data: memberCount } = useOrgMemberCount(dashboardOrgId);
  const { data: revenueTrend, isError: revenueTrendError } = useOrgRevenueTrend(
    dashboardOrgId,
    trendRange.start,
    trendRange.end,
  );
  const { data: memberTrend } = useOrgMemberTrend(
    dashboardOrgId,
    trendRange.start,
    trendRange.end,
  );

  const totalRevenue = parseDecimal(revenue?.revenue);
  const totalMembers = memberCount?.member_count ?? 0;
  const facilityTotal = facilities?.length ?? 0;
  const activeFacilityTotal =
    facilities?.filter((f) => f.status === "active").length ?? 0;

  // Zero-data → a single honest "Now" point (never invented buckets/figures).
  const revenuePoints = useMemo<TrendPoint[]>(() => {
    return (revenueTrend?.points ?? []).flatMap((point) => {
      const value = parseDecimal(point.revenue);
      return value === null ? [] : [{ label: monthLabel(point.end), value }];
    });
  }, [revenueTrend?.points]);

  const memberPoints = useMemo<TrendPoint[]>(() => {
    const points = memberTrend?.points.map((p) => ({
      label: monthLabel(p.end),
      value: p.member_count,
    }));
    return points && points.length > 0
      ? points
      : [{ label: "Now", value: totalMembers }];
  }, [memberTrend?.points, totalMembers]);

  if (orgView.kind === "loading") {
    return (
      <Box
        role="status"
        sx={{
          alignItems: "center",
          display: "flex",
          justifyContent: "center",
          minHeight: "40vh",
        }}
      >
        <CircularProgress aria-label="Loading organization dashboard" />
      </Box>
    );
  }

  // Self-guard: this org-level page owns its own approval check.
  if (!canView) {
    return <Navigate to="/getting-started" replace />;
  }

  return (
    <Stack spacing={2.5} sx={{ px: { xs: 2, lg: 3 }, pb: 4 }}>
      {revenueError || revenueTrendError ? (
        <Alert severity="error">
          Revenue is unavailable. Use settlements for per-currency totals or try
          again.
        </Alert>
      ) : null}
      <Typography
        component="h1"
        sx={{
          height: "1px",
          margin: -1,
          overflow: "hidden",
          position: "absolute",
          width: "1px",
          clip: "rect(0 0 0 0)",
        }}
      >
        Organization dashboard
      </Typography>

      <Box
        sx={{
          display: "grid",
          gap: 2,
          gridTemplateColumns: { xs: "1fr", xl: "repeat(2, minmax(0, 1fr))" },
        }}
      >
        <TrendCard
          label="Revenue"
          value={
            revenueError || !revenue?.currency || totalRevenue === null
              ? "—"
              : formatMoney(totalRevenue, revenue.currency)
          }
          data={revenuePoints}
          color={theme.palette.primary.main}
          formatTooltip={(v) =>
            revenueTrend?.currency ? formatMoney(v, revenueTrend.currency) : "—"
          }
        />
        <TrendCard
          label="Members"
          value={numberFormatter.format(totalMembers)}
          data={memberPoints}
          color={theme.palette.warning.main}
          formatTooltip={(v) => numberFormatter.format(v)}
        />
      </Box>

      <Paper variant="outlined" sx={{ overflow: "hidden" }}>
        <Stack
          direction="row"
          spacing={1.5}
          sx={{
            alignItems: "center",
            flexWrap: "wrap",
            justifyContent: "space-between",
            px: { xs: 2, lg: 3 },
            py: 1.5,
          }}
        >
          <Typography component="h2" variant="h6" sx={{ fontWeight: 800 }}>
            Facilities
          </Typography>
          <Stack direction="row" spacing={1} sx={{ flexWrap: "wrap" }}>
            <Chip
              label={`${facilityTotal} ${facilityTotal === 1 ? "facility" : "facilities"}`}
              size="small"
            />
            <Chip
              color="primary"
              label={`${activeFacilityTotal} active`}
              size="small"
              variant="outlined"
            />
          </Stack>
        </Stack>

        <Divider />

        {facilitiesLoading ? (
          <Stack
            role="status"
            spacing={1}
            sx={{ alignItems: "center", px: { xs: 2, lg: 3 }, py: 5 }}
          >
            <CircularProgress size={22} />
            <Typography color="text.secondary" variant="body2">
              Loading facilities…
            </Typography>
          </Stack>
        ) : !facilities || facilities.length === 0 ? (
          <Box sx={{ px: { xs: 2, lg: 3 }, py: 5, textAlign: "center" }}>
            <Typography sx={{ fontWeight: 700 }}>No facilities yet</Typography>
            <Typography color="text.secondary" variant="body2" sx={{ mt: 0.5 }}>
              Create your first facility to get started.
            </Typography>
          </Box>
        ) : (
          <Stack divider={<Divider flexItem />}>
            {facilities.map((f) => (
              <FacilityRow
                key={f.facility_id}
                facility={f}
                onSelect={() => {
                  setFacility(f.facility_id, f.name, f.facility_type);
                  navigate("/dashboard");
                }}
              />
            ))}
          </Stack>
        )}

        <Divider />
        <Box
          sx={{
            display: "flex",
            justifyContent: "flex-end",
            px: { xs: 2, lg: 3 },
            py: 1.5,
          }}
        >
          <Button
            size="small"
            onClick={() => navigate("/facilities/new")}
            startIcon={<AddIcon />}
            variant="contained"
            sx={{ width: { xs: "100%", sm: "auto" } }}
          >
            Create New Facility
          </Button>
        </Box>
      </Paper>
    </Stack>
  );
}
